import { v4 as uuidv4 } from 'uuid';
import { EditorWord, wordsToPlainText } from '../types';

/**
 * Creates a new word with default values and uninitialized timing
 * @param text - The text content of the word
 * @returns A new EditorWord with generated ID and placeholder timing
 */
const createNewEditorWord = (text: string): EditorWord => {
  return {
    id: uuidv4(),
    text,
    startTime: -1, // Will be calculated later
    endTime: -1, // Will be calculated later
    position: 0, // Will be set by parent
    confidenceScore: 1,
    hasEntities: false,
    isNew: true,
    lastModifiedAt: Date.now(),
  };
};

interface TextDiffResult {
  newEditorWords: EditorWord[];
  hasChanges: boolean;
}

/**
 * Advanced text diffing that creates final word array for backend
 * Preserves existing word IDs and generates permanent UUIDs for new words
 *
 * @param originalWords - Original word array with IDs
 * @param originalText - Original text content
 * @param newText - Edited text content
 * @returns Final word array with preserved/permanent IDs for backend sync
 */

/**
 * Find Longest Common Subsequence of unchanged words between old and new text
 * Returns array of {oldIndex, newIndex} pairs for matching words
 */
function findUnchangedWords(
  originalWords: EditorWord[],
  originalTokens: string[],
  newTokens: string[]
): Array<{ oldIndex: number; newIndex: number; word: EditorWord }> {
  // Finding unchanged words using LCS algorithm
  // Old words processing
  // New words processing

  const unchanged: Array<{
    oldIndex: number;
    newIndex: number;
    word: EditorWord;
  }> = [];

  // Create LCS matrix
  const dp: number[][] = Array(originalTokens.length + 1)
    .fill(null)
    .map(() => Array(newTokens.length + 1).fill(0));

  // Fill LCS matrix
  for (let i = 1; i <= originalTokens.length; i++) {
    for (let j = 1; j <= newTokens.length; j++) {
      if (originalTokens[i - 1] === newTokens[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to find the actual LCS
  let i = originalTokens.length;
  let j = newTokens.length;

  while (i > 0 && j > 0) {
    if (originalTokens[i - 1] === newTokens[j - 1]) {
      // Found a matching word - add to unchanged list
      unchanged.unshift({
        oldIndex: i - 1,
        newIndex: j - 1,
        word: originalWords[i - 1],
      });
      i--;
      j--;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }

  // Unchanged words found
  return unchanged;
}

/**
 * Process words between anchor points to determine updates/insertions/deletions
 */
function processSegment(
  originalWords: EditorWord[],
  originalTokens: string[],
  newTokens: string[],
  oldStart: number,
  oldEnd: number,
  newStart: number,
  newEnd: number
): {
  words: EditorWord[];
  hasChanges: boolean;
} {
  const words: EditorWord[] = [];
  let hasChanges = false;

  const oldSegment = originalTokens.slice(oldStart, oldEnd);
  const newSegment = newTokens.slice(newStart, newEnd);
  const originalSegment = originalWords.slice(oldStart, oldEnd);

  if (newSegment.length === 0) {
    // All words deleted
    hasChanges = true;
  } else if (oldSegment.length === 0) {
    // All words inserted
    newSegment.forEach((text, index) => {
      const newEditorWord = createNewEditorWord(text);
      // Word added
      words.push(newEditorWord);
      hasChanges = true;
    });
  } else if (newSegment.length === 1 && oldSegment.length === 1) {
    // Regular update - preserve the ID
    const textChanged = originalSegment[0].text !== newSegment[0];
    const updatedWord: EditorWord = {
      ...originalSegment[0],
      text: newSegment[0],
      isModified: textChanged,
      ...(textChanged && { lastModifiedAt: Date.now(), confidenceScore: 1 }),
    };
    // Word updated
    words.push(updatedWord);
    hasChanges = textChanged;
  } else if (oldSegment.length === 1 && newSegment.length === 2) {
    // Check for word split: one word becomes two
    const originalText = oldSegment[0];
    const combinedNew = newSegment.join('');

    if (originalText === combinedNew) {
      // It's a split! First part keeps original ID, second part gets new ID
      // Word split detected

      const originalWord = originalSegment[0];
      const firstPartText = newSegment[0];
      const secondPartText = newSegment[1];

      // Calculate timing split based on character proportions
      const totalLength = originalText.length;
      const firstPartLength = firstPartText.length;

      const originalDuration = originalWord.endTime - originalWord.startTime;
      const firstPartDuration =
        (originalDuration * firstPartLength) / totalLength;
      const splitTime = parseFloat(
        (originalWord.startTime + firstPartDuration).toFixed(6)
      );

      // First part keeps the original word's ID with proportional timing
      const firstPart: EditorWord = {
        ...originalWord,
        text: firstPartText,
        endTime: splitTime,
        isModified: true,
        lastModifiedAt: Date.now(),
        confidenceScore: 1,
      };
      words.push(firstPart);
      hasChanges = true;

      // Second part gets a new ID with remaining timing
      const secondPart: EditorWord = {
        ...createNewEditorWord(secondPartText),
        startTime: splitTime,
        endTime: originalWord.endTime,
        confidenceScore: 1,
      };
      words.push(secondPart);
      hasChanges = true;
    } else {
      // Not a split, treat as delete + inserts
      hasChanges = true;

      newSegment.forEach((text, index) => {
        const newEditorWord = createNewEditorWord(text);
        // Word added
        words.push(newEditorWord);
      });
    }
  } else if (oldSegment.length === 2 && newSegment.length === 1) {
    // Check for word merge: two words become one
    const combinedOld = oldSegment.join('');
    const newText = newSegment[0];

    if (combinedOld === newText) {
      // It's a merge! Keep the first word's ID
      // Word merge detected

      const mergedWord: EditorWord = {
        ...originalSegment[0],
        text: newText,
        isModified: true,
        lastModifiedAt: Date.now(),
        confidenceScore: 1,
      };
      words.push(mergedWord);
      hasChanges = true;
    } else {
      // Not a merge, treat as deletes + insert
      hasChanges = true;

      const newEditorWord = createNewEditorWord(newText);
      // Word added
      words.push(newEditorWord);
    }
  } else {
    // Complex case - try to match by position when word counts are equal
    if (oldSegment.length === newSegment.length) {
      // Same number of words - do positional matching
      for (let i = 0; i < oldSegment.length; i++) {
        // Update existing word
        const textChanged = originalSegment[i].text !== newSegment[i];
        const updatedWord: EditorWord = {
          ...originalSegment[i],
          text: newSegment[i],
          isModified: textChanged,
          ...(textChanged && {
            lastModifiedAt: Date.now(),
            confidenceScore: 1,
          }),
        };
        // Word updated
        words.push(updatedWord);
        if (textChanged) hasChanges = true;
      }
    } else {
      // Different word counts - treat as deletions + insertions
      hasChanges = true;

      newSegment.forEach((text, index) => {
        const newEditorWord = createNewEditorWord(text);
        // Word added
        words.push(newEditorWord);
      });
    }
  }

  return { words, hasChanges };
}

// Helper function to calculate timing for new words based on neighbors
const calculateTimingForNewWords = (
  originalWords: EditorWord[],
  newEditorWords: EditorWord[],
  utteranceStartTime: number,
  utteranceEndTime: number
): void => {
  // Identify groups of consecutive new words (words without timing)
  const newWordGroups: Array<{
    startIdx: number;
    endIdx: number;
  }> = [];

  let currentGroup: { startIdx: number; endIdx: number } | null = null;

  newEditorWords.forEach((word, index) => {
    const isNewWord = word.startTime === -1 || word.endTime === -1;

    if (isNewWord) {
      if (!currentGroup) {
        currentGroup = { startIdx: index, endIdx: index };
      } else {
        currentGroup.endIdx = index;
      }
    } else if (currentGroup) {
      newWordGroups.push(currentGroup);
      currentGroup = null;
    }
  });

  // Don't forget the last group
  if (currentGroup) {
    newWordGroups.push(currentGroup);
  }

  // Calculate timing for each group
  newWordGroups.forEach((group) => {
    const wordCount = group.endIdx - group.startIdx + 1;
    let groupStartTime: number;
    let groupEndTime: number;

    // Get timing from neighbors
    const prevWord =
      group.startIdx > 0 ? newEditorWords[group.startIdx - 1] : null;
    const nextWord =
      group.endIdx < newEditorWords.length - 1
        ? newEditorWords[group.endIdx + 1]
        : null;

    if (
      prevWord &&
      prevWord.endTime !== -1 &&
      nextWord &&
      nextWord.startTime !== -1
    ) {
      // Words in middle - interpolate between neighbors
      groupStartTime = prevWord.endTime;
      groupEndTime = nextWord.startTime;
    } else if (prevWord && prevWord.endTime !== -1) {
      // Words at end - use previous word's end time
      groupStartTime = prevWord.endTime;
      groupEndTime = prevWord.endTime;
    } else if (nextWord && nextWord.startTime !== -1) {
      // Words at beginning - distribute before next word
      groupStartTime = Math.max(0, nextWord.startTime - wordCount * 0.5);
      groupEndTime = nextWord.startTime;
    } else if (originalWords.length > 0) {
      // No neighbors but have original words - use their range
      const firstOriginal = originalWords[0];
      const lastOriginal = originalWords[originalWords.length - 1];
      if (firstOriginal.startTime !== -1 && lastOriginal.endTime !== -1) {
        groupStartTime = firstOriginal.startTime;
        groupEndTime = lastOriginal.endTime;
      } else {
        // Fallback: use utterance times
        groupStartTime = utteranceStartTime;
        groupEndTime = utteranceEndTime;
      }
    } else {
      // Empty utterance - use utterance's start/end times
      groupStartTime = utteranceStartTime;
      groupEndTime = utteranceEndTime;
    }

    // Distribute timing across words in the group
    if (groupStartTime === groupEndTime) {
      // All words get the same timestamp (words at end of utterance)
      for (let i = group.startIdx; i <= group.endIdx; i++) {
        newEditorWords[i].startTime = groupStartTime;
        newEditorWords[i].endTime = groupEndTime;
        newEditorWords[i].confidenceScore =
          newEditorWords[i].confidenceScore || 0;
        newEditorWords[i].hasEntities = newEditorWords[i].hasEntities || false;
      }
    } else {
      // Distribute time with higher precision
      const totalDuration = groupEndTime - groupStartTime;
      const timePerWord = totalDuration / wordCount;

      for (let i = group.startIdx; i <= group.endIdx; i++) {
        const positionInGroup = i - group.startIdx;
        // Use higher precision timing calculations
        newEditorWords[i].startTime = parseFloat(
          (groupStartTime + positionInGroup * timePerWord).toFixed(6)
        );
        newEditorWords[i].endTime = parseFloat(
          (groupStartTime + (positionInGroup + 1) * timePerWord).toFixed(6)
        );
        newEditorWords[i].confidenceScore =
          newEditorWords[i].confidenceScore || 0;
        newEditorWords[i].hasEntities = newEditorWords[i].hasEntities || false;
      }
    }
  });
};

export const reconcileEditedText = (
  originalWords: EditorWord[],
  newText: string,
  utteranceStartTime: number,
  utteranceEndTime: number
): TextDiffResult => {
  // Text diff analysis

  // Derive old text from original words
  const originalText = wordsToPlainText(originalWords);

  // Handle no change case
  if (originalText.trim() === newText.trim()) {
    // No changes detected - returning original word array
    return {
      newEditorWords: originalWords,
      hasChanges: false,
    };
  }

  // Split texts into tokens (words)
  const originalTokens = originalWords.map((w) => w.text) || [];
  const newTokens = newText.match(/\S+/g) || [];

  // Building final word array using LCS-based algorithm

  // Step 1: Find unchanged words using LCS
  const unchangedWords = findUnchangedWords(
    originalWords,
    originalTokens,
    newTokens
  );

  const newEditorWords: EditorWord[] = [];
  let hasChanges = false;

  // Step 2: Process segments between unchanged words
  let oldPos = 0;
  let newPos = 0;

  for (const anchor of unchangedWords) {
    // Process segment before this anchor
    if (oldPos < anchor.oldIndex || newPos < anchor.newIndex) {
      const segment = processSegment(
        originalWords,
        originalTokens,
        newTokens,
        oldPos,
        anchor.oldIndex,
        newPos,
        anchor.newIndex
      );
      newEditorWords.push(...segment.words);
      if (segment.hasChanges) hasChanges = true;
    }

    // Add the unchanged anchor word
    // Unchanged word preserved
    newEditorWords.push(anchor.word);

    // Move positions past the anchor
    oldPos = anchor.oldIndex + 1;
    newPos = anchor.newIndex + 1;
  }

  // Process final segment after last anchor
  if (oldPos < originalTokens.length || newPos < newTokens.length) {
    const segment = processSegment(
      originalWords,
      originalTokens,
      newTokens,
      oldPos,
      originalTokens.length,
      newPos,
      newTokens.length
    );
    newEditorWords.push(...segment.words);
    if (segment.hasChanges) hasChanges = true;
  }

  // Final word array built
  // Calculate timing for any new words that were created
  if (hasChanges) {
    calculateTimingForNewWords(
      originalWords,
      newEditorWords,
      utteranceStartTime,
      utteranceEndTime
    );
  }

  return {
    newEditorWords,
    hasChanges,
  };
};
